import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IAccountMappingTemplate,
    IAccountMappingTemplateGroup,
    IAccountMappingTemplateGroupItemFLI,
    ICashFlowStatement,
    ItemType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { EqualityChecksResult } from '../../../enums/equality-checks-results.enum';
import { templateConstants } from '../../../shared/template-constants';

export class EqualityChecksHelper {
    static checkAssetsEqualLiabilities(balanceTemplate: IAccountMappingTemplate): EqualityChecksResult {
        if (balanceTemplate.group.groups.length < templateConstants.financialStatementMinimumLength) {
            throw new Error("Balance template doesn't contain assets or liabilities");
        }

        const asstes = balanceTemplate.group.groups[templateConstants.assetsGroupIndex];
        const liabilities = balanceTemplate.group.groups[templateConstants.liabilitiesGroupIndex];

        let assetsClosingBalance: number | undefined;
        if (asstes.calculatedClosingBalance != null && NumberUtils.isFinite(asstes.calculatedClosingBalance)) {
            assetsClosingBalance = NumberUtils.roundNumber(asstes.calculatedClosingBalance);
        }

        let assetsOpeningBalance: number | undefined;
        if (asstes.calculatedOpeningBalance != null && NumberUtils.isFinite(asstes.calculatedOpeningBalance)) {
            assetsOpeningBalance = NumberUtils.roundNumber(asstes.calculatedOpeningBalance);
        }

        let liabilitiesClosingBalance: number | undefined;
        if (
            liabilities.calculatedClosingBalance != null &&
            NumberUtils.isFinite(liabilities.calculatedClosingBalance)
        ) {
            liabilitiesClosingBalance = NumberUtils.roundNumber(liabilities.calculatedClosingBalance);
        }

        let liabilitiesOpeningBalance: number | undefined;
        if (
            liabilities.calculatedOpeningBalance != null &&
            NumberUtils.isFinite(liabilities.calculatedOpeningBalance)
        ) {
            liabilitiesOpeningBalance = NumberUtils.roundNumber(liabilities.calculatedOpeningBalance);
        }

        if (
            assetsClosingBalance == null ||
            assetsOpeningBalance == null ||
            liabilitiesClosingBalance == null ||
            liabilitiesOpeningBalance == null
        ) {
            throw new Error('Assets or liabilities closing or openeing balance is undefined');
        }

        const closingEqualityCheck = NumberUtils.subtractNumbers(assetsClosingBalance, liabilitiesClosingBalance);
        const openingEqualityCheck = NumberUtils.subtractNumbers(assetsOpeningBalance, liabilitiesOpeningBalance);

        if (!NumberUtils.isZero(closingEqualityCheck) && NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.CLOSING_NOT_EQUAL;
        }

        if (NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.OPENING_NOT_EQUAL;
        }

        if (!NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.BOTH_NOT_EQUAL;
        }

        return EqualityChecksResult.BOTH_EQUAL;
    }

    static findProfitFliInBalance(
        balanceTemplate: IAccountMappingTemplate,
    ): IAccountMappingTemplateGroupItemFLI | null {
        if (balanceTemplate.profitOfTheYear == null) {
            return null;
        }
        return this.getProfitFliOfBalance(balanceTemplate.group, balanceTemplate.profitOfTheYear);
    }

    static checkProfitIncomeEqualProfitFLI(
        balanceTemplate: IAccountMappingTemplate,
        incomeTemplate: IAccountMappingTemplate,
    ): EqualityChecksResult {
        if (balanceTemplate.profitOfTheYear == null) {
            // if no profit fli is defined, then equality should pass
            return EqualityChecksResult.BOTH_EQUAL;
        }
        const profitOfTheYearFliName = balanceTemplate.profitOfTheYear;
        const balanceProfitFli = this.getProfitFliOfBalance(balanceTemplate.group, profitOfTheYearFliName);
        if (balanceProfitFli == null) {
            return EqualityChecksResult.BOTH_EQUAL;
        }

        let profitIncomeClosingBalance: number | undefined;
        if (
            incomeTemplate.group.calculatedClosingBalance != null &&
            NumberUtils.isFinite(incomeTemplate.group.calculatedClosingBalance)
        ) {
            profitIncomeClosingBalance = NumberUtils.roundNumber(incomeTemplate.group.calculatedClosingBalance);
        } else {
            // This means, that the value is not defined, because no accoutns are mapped to the group
            profitIncomeClosingBalance = 0;
        }

        let profitIncomeOpeningBalance: number | undefined;
        if (
            incomeTemplate.group.calculatedOpeningBalance != null &&
            NumberUtils.isFinite(incomeTemplate.group.calculatedOpeningBalance)
        ) {
            profitIncomeOpeningBalance = NumberUtils.roundNumber(incomeTemplate.group.calculatedOpeningBalance);
        } else {
            // This means, that the value is not defined, because no accoutns are mapped to the group
            profitIncomeOpeningBalance = 0;
        }

        let profitFliClosingBalance: number | undefined;
        if (
            balanceProfitFli.calculatedClosingBalance != null &&
            NumberUtils.isFinite(balanceProfitFli.calculatedClosingBalance)
        ) {
            profitFliClosingBalance = NumberUtils.roundNumber(balanceProfitFli.calculatedClosingBalance);
        } else {
            // This means, that the value is not defined, because no accoutns are mapped to the group
            profitFliClosingBalance = 0;
        }

        let profitFliOpeningBalance: number | undefined;
        if (
            balanceProfitFli.calculatedOpeningBalance != null &&
            NumberUtils.isFinite(balanceProfitFli.calculatedOpeningBalance)
        ) {
            profitFliOpeningBalance = NumberUtils.roundNumber(balanceProfitFli.calculatedOpeningBalance);
        } else {
            // This means, that the value is not defined, because no accoutns are mapped to the group
            profitFliOpeningBalance = 0;
        }

        if (
            profitIncomeClosingBalance == null ||
            profitIncomeOpeningBalance == null ||
            profitFliClosingBalance == null ||
            profitFliOpeningBalance == null
        ) {
            throw new Error('Profit income or profit fli closing or openeing balance is undefined');
        }

        const closingEqualityCheck = NumberUtils.subtractNumbers(profitIncomeClosingBalance, profitFliClosingBalance);
        const openingEqualityCheck = NumberUtils.subtractNumbers(profitIncomeOpeningBalance, profitFliOpeningBalance);

        if (!NumberUtils.isZero(closingEqualityCheck) && NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.CLOSING_NOT_EQUAL;
        }

        if (NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.OPENING_NOT_EQUAL;
        }

        if (!NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.BOTH_NOT_EQUAL;
        }

        return EqualityChecksResult.BOTH_EQUAL;
    }

    static checkCashFlowStatementTotalsAreEqual(
        cashFlowStatement: ICashFlowStatement,
        currentCashflowStatementTotal: number,
        previousCashflowStatementTotal: number,
    ): EqualityChecksResult {
        const { closingEqualityCheck, openingEqualityCheck } = this.calculateCashFlowStatementTotal(
            cashFlowStatement,
            currentCashflowStatementTotal,
            previousCashflowStatementTotal,
        );

        if (!NumberUtils.isZero(closingEqualityCheck) && NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.CLOSING_NOT_EQUAL;
        }

        if (NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.OPENING_NOT_EQUAL;
        }

        if (!NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.BOTH_NOT_EQUAL;
        }

        return EqualityChecksResult.BOTH_EQUAL;
    }

    static calculateCashFlowStatementTotal(
        cashFlowStatement: ICashFlowStatement,
        currentCashflowStatementTotal: number,
        previousCashflowStatementTotal: number,
    ): {
        closingEqualityCheck: number;
        openingEqualityCheck: number;
    } {
        if (!cashFlowStatement) {
            return { closingEqualityCheck: 0, openingEqualityCheck: 0 };
        }

        const total2ClosingValue = NumberUtils.subtractNumbers(
            cashFlowStatement.total.closingCashAndCashEquivalents ?? 0,
            cashFlowStatement.total.openingCashAndCashEquivalents ?? 0,
        );
        const total2OpeningValue = NumberUtils.subtractNumbers(
            cashFlowStatement.total.previousClosingCashAndCashEquivalents ?? 0,
            cashFlowStatement.total.previousOpeningCashAndCashEquivalents ?? 0,
        );

        const closingEqualityCheck = NumberUtils.subtractNumbers(currentCashflowStatementTotal, total2ClosingValue);
        const openingEqualityCheck = NumberUtils.subtractNumbers(previousCashflowStatementTotal, total2OpeningValue);

        return { closingEqualityCheck, openingEqualityCheck };
    }

    static checkTotalReclassesAssetsEqualTotalReclassesLiabilities(
        balanceTemplate: IAccountMappingTemplate,
    ): EqualityChecksResult {
        if (balanceTemplate.group.groups.length < 2) {
            throw new Error("Balance template doesn't contain assets or liabilities");
        }

        const assets = balanceTemplate.group.groups[templateConstants.assetsGroupIndex];
        const liabilities = balanceTemplate.group.groups[templateConstants.liabilitiesGroupIndex];

        let reclassesAssetsClosingBalance = 0;
        if (assets.closingReClass != null && NumberUtils.isFinite(assets.closingReClass)) {
            reclassesAssetsClosingBalance = NumberUtils.roundNumber(assets.closingReClass);
        }

        let reclassesAssetsOpeningBalance = 0;
        if (assets.openingReClass != null && NumberUtils.isFinite(assets.openingReClass)) {
            reclassesAssetsOpeningBalance = NumberUtils.roundNumber(assets.openingReClass);
        }

        let reclassesLiabilitiesClosingBalance = 0;
        if (liabilities.closingReClass != null && NumberUtils.isFinite(liabilities.closingReClass)) {
            reclassesLiabilitiesClosingBalance = NumberUtils.roundNumber(liabilities.closingReClass);
        }

        let reclassesLiabilitiesOpeningBalance = 0;
        if (liabilities.openingReClass != null && NumberUtils.isFinite(liabilities.openingReClass)) {
            reclassesLiabilitiesOpeningBalance = NumberUtils.roundNumber(liabilities.openingReClass);
        }

        const closingEqualityCheck = NumberUtils.subtractNumbers(
            reclassesAssetsClosingBalance,
            reclassesLiabilitiesClosingBalance,
        );
        const openingEqualityCheck = NumberUtils.subtractNumbers(
            reclassesAssetsOpeningBalance,
            reclassesLiabilitiesOpeningBalance,
        );

        if (!NumberUtils.isZero(closingEqualityCheck) && NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.CLOSING_NOT_EQUAL;
        }

        if (NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.OPENING_NOT_EQUAL;
        }

        if (!NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.BOTH_NOT_EQUAL;
        }

        return EqualityChecksResult.BOTH_EQUAL;
    }

    static checkTotalAdjustmentsAssetsEqualTotalAdjustmentsLiabilities(
        balanceTemplate: IAccountMappingTemplate,
    ): EqualityChecksResult {
        if (balanceTemplate.group.groups.length < 2) {
            throw new Error("Balance template doesn't contain assets or liabilities");
        }
        const assets = balanceTemplate.group.groups[templateConstants.assetsGroupIndex];
        const liabilities = balanceTemplate.group.groups[templateConstants.liabilitiesGroupIndex];

        let adjustmentsAssetsClosingBalance = 0;
        if (assets.closingAdjustment != null && NumberUtils.isFinite(assets.closingAdjustment)) {
            adjustmentsAssetsClosingBalance = NumberUtils.roundNumber(assets.closingAdjustment);
        }
        let adjustmentsAssetsOpeningBalance = 0;
        if (assets.openingAdjustment != null && NumberUtils.isFinite(assets.openingAdjustment)) {
            adjustmentsAssetsOpeningBalance = NumberUtils.roundNumber(assets.openingAdjustment);
        }

        let adjustmentsLiabilitiesClosingBalance = 0;
        if (liabilities.closingAdjustment != null && NumberUtils.isFinite(liabilities.closingAdjustment)) {
            adjustmentsLiabilitiesClosingBalance = NumberUtils.roundNumber(liabilities.closingAdjustment);
        }

        let adjustmentsLiabilitiesOpeningBalance = 0;
        if (liabilities.openingAdjustment != null && NumberUtils.isFinite(liabilities.openingAdjustment)) {
            adjustmentsLiabilitiesOpeningBalance = NumberUtils.roundNumber(liabilities.openingAdjustment);
        }

        const closingEqualityCheck = NumberUtils.subtractNumbers(
            adjustmentsAssetsClosingBalance,
            adjustmentsLiabilitiesClosingBalance,
        );
        const openingEqualityCheck = NumberUtils.subtractNumbers(
            adjustmentsAssetsOpeningBalance,
            adjustmentsLiabilitiesOpeningBalance,
        );

        if (!NumberUtils.isZero(closingEqualityCheck) && NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.CLOSING_NOT_EQUAL;
        }

        if (NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.OPENING_NOT_EQUAL;
        }

        if (!NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.BOTH_NOT_EQUAL;
        }

        return EqualityChecksResult.BOTH_EQUAL;
    }

    static checkTotalReclassesIncomeEqualTotalReclassesProfitFLI(
        balanceTemplate: IAccountMappingTemplate,
        incomeTemplate: IAccountMappingTemplate,
    ): EqualityChecksResult {
        if (balanceTemplate.profitOfTheYear == null) {
            // if no profit fli is defined, then equality should pass
            return EqualityChecksResult.BOTH_EQUAL;
        }
        const profitOfTheYearFliName = balanceTemplate.profitOfTheYear;
        const balanceProfitFli = this.getProfitFliOfBalance(balanceTemplate.group, profitOfTheYearFliName);
        if (balanceProfitFli == null) {
            return EqualityChecksResult.BOTH_EQUAL;
        }

        let totalReclassesIncomeClosingBalance = 0;
        if (incomeTemplate.group.closingReClass != null && NumberUtils.isFinite(incomeTemplate.group.closingReClass)) {
            totalReclassesIncomeClosingBalance = NumberUtils.roundNumber(incomeTemplate.group.closingReClass);
        }

        let totalReclassesIncomeOpeningBalance = 0;
        if (incomeTemplate.group.openingReClass != null && NumberUtils.isFinite(incomeTemplate.group.openingReClass)) {
            totalReclassesIncomeOpeningBalance = NumberUtils.roundNumber(incomeTemplate.group.openingReClass);
        }

        let totalReclassesProfitFliClosingBalance = 0;
        if (balanceProfitFli.closingReClass != null && NumberUtils.isFinite(balanceProfitFli.closingReClass)) {
            totalReclassesProfitFliClosingBalance = NumberUtils.roundNumber(balanceProfitFli.closingReClass);
        }

        let totalReclassesProfitFliOpeningBalance = 0;
        if (balanceProfitFli.openingReClass != null && NumberUtils.isFinite(balanceProfitFli.openingReClass)) {
            totalReclassesProfitFliOpeningBalance = NumberUtils.roundNumber(balanceProfitFli.openingReClass);
        }

        const closingEqualityCheck = NumberUtils.subtractNumbers(
            totalReclassesIncomeClosingBalance,
            totalReclassesProfitFliClosingBalance,
        );

        const openingEqualityCheck = NumberUtils.subtractNumbers(
            totalReclassesIncomeOpeningBalance,
            totalReclassesProfitFliOpeningBalance,
        );

        if (!NumberUtils.isZero(closingEqualityCheck) && NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.CLOSING_NOT_EQUAL;
        }

        if (NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.OPENING_NOT_EQUAL;
        }

        if (!NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.BOTH_NOT_EQUAL;
        }

        return EqualityChecksResult.BOTH_EQUAL;
    }

    static checkTotalAdjustmentsIncomeEqualTotalAdjustmentsProfitFLI(
        balanceTemplate: IAccountMappingTemplate,
        incomeTemplate: IAccountMappingTemplate,
    ): EqualityChecksResult {
        if (balanceTemplate.profitOfTheYear == null) {
            // if no profit fli is defined, then equality should pass
            return EqualityChecksResult.BOTH_EQUAL;
        }
        const profitOfTheYearFliName = balanceTemplate.profitOfTheYear;
        const balanceProfitFli = this.getProfitFliOfBalance(balanceTemplate.group, profitOfTheYearFliName);
        if (balanceProfitFli == null) {
            return EqualityChecksResult.BOTH_EQUAL;
        }

        let totalAdjustmentsIncomeClosingBalance = 0;
        if (
            incomeTemplate.group.closingAdjustment != null &&
            NumberUtils.isFinite(incomeTemplate.group.closingAdjustment)
        ) {
            totalAdjustmentsIncomeClosingBalance = NumberUtils.roundNumber(incomeTemplate.group.closingAdjustment);
        }

        let totalAdjustmentsIncomeOpeningBalance = 0;
        if (
            incomeTemplate.group.openingAdjustment != null &&
            NumberUtils.isFinite(incomeTemplate.group.openingAdjustment)
        ) {
            totalAdjustmentsIncomeOpeningBalance = NumberUtils.roundNumber(incomeTemplate.group.openingAdjustment);
        }

        let totalAdjustmentsProfitFliClosingBalance = 0;
        if (balanceProfitFli.closingAdjustment != null && NumberUtils.isFinite(balanceProfitFli.closingAdjustment)) {
            totalAdjustmentsProfitFliClosingBalance = NumberUtils.roundNumber(balanceProfitFli.closingAdjustment);
        }

        let totalAdjustmentsProfitFliOpeningBalance = 0;
        if (balanceProfitFli.openingAdjustment != null && NumberUtils.isFinite(balanceProfitFli.openingAdjustment)) {
            totalAdjustmentsProfitFliOpeningBalance = NumberUtils.roundNumber(balanceProfitFli.openingAdjustment);
        }

        const closingEqualityCheck = NumberUtils.subtractNumbers(
            totalAdjustmentsIncomeClosingBalance,
            totalAdjustmentsProfitFliClosingBalance,
        );
        const openingEqualityCheck = NumberUtils.subtractNumbers(
            totalAdjustmentsIncomeOpeningBalance,
            totalAdjustmentsProfitFliOpeningBalance,
        );

        if (!NumberUtils.isZero(closingEqualityCheck) && NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.CLOSING_NOT_EQUAL;
        }

        if (NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.OPENING_NOT_EQUAL;
        }

        if (!NumberUtils.isZero(closingEqualityCheck) && !NumberUtils.isZero(openingEqualityCheck)) {
            return EqualityChecksResult.BOTH_NOT_EQUAL;
        }

        return EqualityChecksResult.BOTH_EQUAL;
    }

    private static getProfitFliOfBalance(
        templateGroup: IAccountMappingTemplateGroup,
        profitOfTheYearFliName: string,
    ): IAccountMappingTemplateGroupItemFLI | null {
        for (const group of templateGroup.groups) {
            const profitFli = this.getProfitFliInGroup(group, profitOfTheYearFliName);
            if (profitFli != null) {
                return profitFli;
            }
        }
        const profitFli = this.getProfitFliInGroup(templateGroup, profitOfTheYearFliName);
        if (profitFli != null) {
            return profitFli;
        }
        return null;
    }

    private static getProfitFliInGroup(
        templateGroup: IAccountMappingTemplateGroup,
        profitOfTheYearFliName: string,
    ): IAccountMappingTemplateGroupItemFLI | null {
        for (const group of templateGroup.groups) {
            const profitOfTheYearFli = group.items.find((fli) => fli.name === profitOfTheYearFliName);
            if (profitOfTheYearFli != null && profitOfTheYearFli.type === ItemType.FLI) {
                return profitOfTheYearFli;
            }
            if (group.groups.length === 0) {
                continue;
            }
            const balanceProfitFli = this.getProfitFliInGroup(group, profitOfTheYearFliName);
            if (balanceProfitFli != null) {
                return balanceProfitFli;
            }
        }
        return null;
    }
}
